import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { api, getErrorMessage } from "@/api";
import { useAuth } from "@/auth";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [preferences, setPreferences] = useState({ reminder_48: true, reminder_24: true });
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const refresh = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setHasMore(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 100 } });
      setNotifications(data);
      setHasMore(data.length === 100);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]);
  const loadPreferences = useCallback(async () => {
    if (!user || user.role !== "artista") return;
    const { data } = await api.get("/notifications/preferences");
    setPreferences(data);
  }, [user]);
  useEffect(() => {
    Promise.all([refresh(), loadPreferences()]).catch(() => null);
  }, [refresh, loadPreferences]);
  const markRead = async (notification) => {
    if (notification.read || notification.is_own === false) return;
    try {
      const { data } = await api.post(`/notifications/${notification.id}/read`);
      setNotifications((current) => current.map((item) => item.id === data.id ? data : item));
    } catch (error) { throw new Error(getErrorMessage(error)); }
  };
  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((current) => current.map((item) => ({ ...item, read: true })));
    } catch (error) { throw new Error(getErrorMessage(error)); }
  };
  const removeNotification = async (notification) => {
    try {
      await api.delete(`/notifications/${notification.id}`);
      setNotifications((current) => current.filter((item) => item.id !== notification.id));
    } catch (error) { throw new Error(getErrorMessage(error)); }
  };
  const loadMore = async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 100, offset: notifications.length } });
      setNotifications((current) => [...current, ...data]);
      setHasMore(data.length === 100);
    } finally {
      setLoading(false);
    }
  };
  const updatePreferences = async (nextPreferences) => {
    setPreferencesSaving(true);
    try {
      const { data } = await api.patch("/notifications/preferences", nextPreferences);
      setPreferences(data);
      await refresh();
    } catch (error) { throw new Error(getErrorMessage(error)); }
    finally { setPreferencesSaving(false); }
  };
  const unreadCount = notifications.filter((notification) => !notification.read && notification.is_own !== false).length;
  return <NotificationContext.Provider value={{ notifications, loading, loadError, refresh, markRead, markAllRead, removeNotification, loadMore, hasMore, unreadCount, preferences, preferencesSaving, updatePreferences }}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  return useContext(NotificationContext);
}